import { Box, MenuItem, TextField } from '@mui/material'
import { APEX_VARIANTS, ApexVariant, Fund, StrategyKey } from '../../api/types'
import { InfoHint } from '../../components/InfoHint'
import { SectionCard } from '../../components/SectionCard'
import { ApexFormState } from './formState'

interface ApexInvestmentSectionProps {
  funds: Fund[]
  state: ApexFormState
  onChange: (patch: Partial<ApexFormState>) => void
  errors: Record<string, string>
}

/**
 * Apexová část: strategie + varianta smlouvy (30 šablon apex-generatoru:
 * 5 strategií × Dohoda/NORM/EB × FO/PO) + částka, účet a vstupní poplatek.
 */
export function ApexInvestmentSection({ funds, state, onChange, errors }: ApexInvestmentSectionProps) {
  // TREASURY nemá v generátoru šablonu — BE takový požadavek vždy odmítne,
  // takže ho v nabídce vůbec neukazujeme (řeší se ručním vkladem).
  const offeredFunds = funds.filter((fund) => fund.key !== 'TREASURY')

  return (
    <SectionCard
      title="Investice"
      info={
        <InfoHint
          title="Pravidla investice"
          lines={[
            'Varianta smlouvy určuje třídu poplatků: EB → A (nižší MF i SF), NORM a Dohoda → B (vyšší).',
            'Vstupní poplatek: „Přičíst k investici" = klient posílá investici + poplatek; „Strhnout" = poplatek se odečte z poslané částky; procento 0–100.',
            'Jednotky se NEpřidělují hned — až po potvrzení platby ve fázi Přidělení jednotek, cenou z ocenění k datu přidělení.',
            'TREASURY se přes generátor zatím nevystavuje (chybí šablona) — použijte ruční vklad.',
          ]}
          sx={{ color: '#fff' }}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
            label="Strategie *"
            value={state.strategy}
            onChange={(event) => onChange({ strategy: event.target.value as StrategyKey })}
            error={Boolean(errors.strategy)}
            helperText={errors.strategy}
          >
            {offeredFunds.map((fund) => (
              <MenuItem key={fund.key} value={fund.key}>
                {fund.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            sx={{ flexGrow: 1, minWidth: 220 }}
            label="Varianta smlouvy *"
            value={state.variant}
            onChange={(event) => onChange({ variant: event.target.value as ApexVariant })}
            error={Boolean(errors.variant)}
            helperText={errors.variant}
          >
            {APEX_VARIANTS.map((variant) => (
              <MenuItem key={variant.key} value={variant.key}>
                {variant.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            sx={{ flexGrow: 1, minWidth: 180 }}
            label="Výše investice (Kč) *"
            value={state.amountText}
            onChange={(event) => onChange({ amountText: event.target.value })}
            error={Boolean(errors.amount)}
            helperText={errors.amount}
            placeholder="1 000 000"
          />
          <TextField
            size="small"
            sx={{ flexGrow: 1, minWidth: 180 }}
            label="Číslo účtu klienta *"
            value={state.accountNumber}
            onChange={(event) => onChange({ accountNumber: event.target.value })}
            error={Boolean(errors.accountNumber)}
            helperText={errors.accountNumber}
            placeholder="123456789/0300"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            sx={{ width: 180, flexGrow: 0 }}
            label="Vstupní poplatek (%)"
            value={state.entryFeePercentText}
            onChange={(event) => onChange({ entryFeePercentText: event.target.value })}
            error={Boolean(errors.entryFeePercent)}
            helperText={errors.entryFeePercent}
          />
          <TextField
            select
            size="small"
            sx={{ flexGrow: 1, minWidth: 220 }}
            label="Režim poplatku"
            value={state.entryFeeMode}
            onChange={(event) =>
              onChange({ entryFeeMode: event.target.value as ApexFormState['entryFeeMode'] })
            }
          >
            <MenuItem value="INCREASE">Přičíst k investici (navýšení)</MenuItem>
            <MenuItem value="DEDUCT">Strhnout z investice</MenuItem>
            <MenuItem value="NONE">Žádný poplatek</MenuItem>
          </TextField>
        </Box>
      </Box>
    </SectionCard>
  )
}
